import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Custom hook for persistent state management with localStorage
 *
 * @template T
 * @param {string} key - The key to store the value under in localStorage
 * @param {T} initialValue - The initial value to use if no value is found in localStorage
 * @param {Object} options - Optional configuration
 * @param {boolean} options.serialize - Whether to serialize/deserialize the value (default: true)
 * @param {Function} options.serializer - Custom serializer function (default: JSON.stringify)
 * @param {Function} options.deserializer - Custom deserializer function (default: JSON.parse)
 * @returns {[T, (value: T | ((val: T) => T)) => void, () => void]} - [storedValue, setValue, removeValue]
 */
const useLocalStorage = (key, initialValue, options = {}) => {
  const {
    serialize = true,
    serializer = JSON.stringify,
    deserializer = JSON.parse,
  } = options;

  // Store the functions in refs to avoid recreating them on each render
  const serializerRef = useRef(serializer);
  const deserializerRef = useRef(deserializer);

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    // Check if localStorage is available
    if (typeof window === "undefined" || !window.localStorage) {
      console.warn(
        "localStorage is not available. Using in-memory state only."
      );
      return initialValue;
    }

    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);

      // Parse stored json or return initialValue if not found
      if (item === null) {
        return initialValue;
      }

      // Return parsed item or item as-is based on serialize option
      return serialize ? deserializerRef.current(item) : item;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Keep track of the key for effects
  const keyRef = useRef(key);

  // Update ref when key changes
  useEffect(() => {
    keyRef.current = key;
  }, [key]);

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = useCallback(
    (value) => {
      try {
        // Allow value to be a function so we have same API as useState
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;

        // Save state
        setStoredValue(valueToStore);

        // Check if localStorage is available
        if (typeof window === "undefined" || !window.localStorage) {
          console.warn(
            "localStorage is not available. Value only stored in state."
          );
          return;
        }

        // Save to localStorage
        const serializedValue = serialize
          ? serializerRef.current(valueToStore)
          : valueToStore;

        window.localStorage.setItem(keyRef.current, serializedValue);
      } catch (error) {
        console.error(
          `Error writing to localStorage key "${keyRef.current}":`,
          error
        );
      }
    },
    [storedValue, serialize]
  );

  // Function to remove the item from localStorage
  const removeValue = useCallback(() => {
    try {
      // Check if localStorage is available
      if (typeof window === "undefined" || !window.localStorage) {
        console.warn("localStorage is not available.");
        return;
      }

      // Remove from localStorage
      window.localStorage.removeItem(keyRef.current);

      // Reset state to initialValue
      setStoredValue(initialValue);
    } catch (error) {
      console.error(
        `Error removing localStorage key "${keyRef.current}":`,
        error
      );
    }
  }, [initialValue]);

  // Update stored value in localStorage if the key changes
  useEffect(() => {
    // Skip if localStorage isn't available
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }

    try {
      const serializedValue = serialize
        ? serializerRef.current(storedValue)
        : storedValue;

      window.localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error(`Error updating localStorage key "${key}":`, error);
    }
  }, [key, storedValue, serialize]);

  return [storedValue, setValue, removeValue];
};

export default useLocalStorage;
